import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { PaymentLinksIcon } from '../../../../assets/svg';
import { Breadcrumb } from '../../../../components/breadcrumb';
import { DateAndTime } from '../../../../components/date-and-time';
import { ViewWrapper } from '../../../../components/view-wrapper';
import { CONSTANTS } from '../../../../config/constants';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { ColumnInterface } from '../../../../models/table';
import { PaymentLinksCustomServices } from '../../../../services/payment-links-custom';
import { defaultFilters, defaultPagination } from '../../../../utils/defaults';
import { ResumeSection } from '../../../home/components/resume-section';
import { ResumeContainer } from '../../../home/styles';
import { DateFilter } from '../../components/custom-pl-table/components/date-filter';
import { PaymentLinkTableSales } from '../../components/custom-pl-table-sales';
import { ExportModal } from '../../components/export-modal';
import { HeadContainer, PLCardIcon, TitlePage } from '../all/styles';
import { SalesTable } from './types';

const getYesterday = (date: Date) => {
	const yesterday = new Date(date);
	yesterday.setDate(date.getDate() - 1);
	return yesterday;
};

const MAXRETRIES = 3;
const MAX_QUERY_BACK_DAYS = 15;

const SalesPage = () => {
	const dictionary = new Map([
		['payment-link-custom', 'Links de pago'],
		['sales', 'Ventas'],
	]);
	const currentDate = new Date();
	const yesterdayDate = getYesterday(currentDate);

	const { width } = useScreenSize();
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();

	const [pagination, setPagination] = useState(defaultPagination);
	const [filters, setFilters] = useState(defaultFilters);

	const [total, setTotal] = useState(0);
	//Modal export sales
	const [modalExport, setModalExport] = useState({
		show: false,
		loading: false,
		url: '',
		countRetry: 0,
		haveError: false,
	});

	const { data: statistics, isLoading: isLoadingStatistics } = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getStatisticsSale,
			merchantSelected?.code,
			filters,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getStatisticsSales(
				merchantSelected?.code as string,
				filters.dateRange?.from ? filters.dateRange?.from : undefined,
				filters.dateRange?.to ? filters.dateRange?.to : undefined,
			),
	});

	const {
		data: table,
		isLoading: isLoadingTable,
		isFetching: isFetchingTable,
	} = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getTableSales,
			merchantSelected?.code,
			pagination,
			filters,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getPaymentTableSales({
				merchantCode: merchantSelected?.code as string,
				pagination,
				filters: {
					...filters,
					stateSelected: filters?.status,
					dateRange: {
						from: filters?.dateRange?.from,
						to: filters?.dateRange?.to,
					},
				},
			}),

		onSuccess: (data) => {
			if (data) setTotal(data.total);
		},
	});

	const getColumns = () => {
		const columns: ColumnInterface<SalesTable>[] = [
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdAt',
				align: 'left',
				cellRender: ({ createdAt }) => <DateAndTime date={createdAt} />,
			},
			{
				sortField: false,
				title: 'Monto recaudado',
				propertyName: 'amountCollected',
				align: 'left',
				cellRender: ({ amountCollected }) => <>{amountCollected}</>,
			},
			{
				sortField: false,
				title: 'Conversión',
				propertyName: 'conversion',
				align: 'left',
				cellRender: ({ conversion }) => <>{conversion}</>,
			},
			{
				sortField: false,
				title: 'Cobros exitosos',
				propertyName: 'success',
				align: 'left',
				cellRender: ({ success }) => <>{success}</>,
			},
		];

		return columns;
	};

	const handleCloseExportModal = () => {
		setModalExport({
			...modalExport,
			show: false,
			loading: false,
		});
	};

	const handleExportAction = async () => {
		setModalExport({
			...modalExport,
			loading: true,
			show: true,
		});

		try {
			const response = await PaymentLinksCustomServices.getExportPaymentDataSales({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
			});

			if (response.url) {
				setModalExport({
					...modalExport,
					show: true,
					loading: false,
					haveError: false,
					countRetry: 0,
					url: response.url as string,
				});
			}
		} catch (e: any) {
			console.error(e);
			handleShowAlert({
				type: 'error',
				message: 'Hubo un error al exportar los datos, intenta nuevamente',
			});
			setModalExport({
				...modalExport,
				loading: false,
				show: true,
				haveError: true,
				countRetry: modalExport.countRetry + 1,
				url: '',
			});
		}
	};

	useEffect(() => {
		setFilters(defaultFilters);
		setPagination(defaultPagination);
	}, [merchantSelected]);

	return (
		<ViewWrapper title="Payment Links" isLoading={false}>
			{modalExport.show && (
				<ExportModal
					title="Descargar ventas"
					isOpen={modalExport.show}
					isLoading={modalExport.loading}
					retryError={modalExport.haveError && modalExport.countRetry < MAXRETRIES}
					closeError={modalExport.haveError && modalExport.countRetry >= MAXRETRIES}
					onClose={handleCloseExportModal}
					linkExport={modalExport.url}
					retryFunction={handleExportAction}
				/>
			)}
			<HeadContainer>
				<PLCardIcon selected>
					<TitlePage>
						<PaymentLinksIcon />
					</TitlePage>
				</PLCardIcon>
				<Breadcrumb dictionary={dictionary} />
				<DateFilter
					filters={filters}
					onFilterChange={setFilters}
					wrapText={width < 1024}
					pagination={pagination}
					setPagination={setPagination}
					timeLabelText="Horario corriente"
					queryDaysBack={MAX_QUERY_BACK_DAYS}
					pastDate={yesterdayDate}
					today={currentDate}
					formatDate={true}
				/>
			</HeadContainer>
			<ResumeContainer>
				<ResumeSection data={statistics} isLoading={isLoadingStatistics} />
			</ResumeContainer>
			<PaymentLinkTableSales
				allowExport={true}
				exportWithSearch={true}
				handleExportAction={handleExportAction}
				data={table?.tableInfo || []}
				columns={getColumns()}
				filters={filters}
				hasFilters={true}
				isLoading={isLoadingTable || isFetchingTable}
				hasDateFilters={false}
				hasSearch={false}
				onFilterChange={setFilters}
				pagination={pagination}
				setPagination={setPagination}
				totalResults={total}
			/>
		</ViewWrapper>
	);
};

export { SalesPage };
